<template>
  <FormField
    :label="label"
    :labelFor="defaultId"
    :error="error"
    :success="success"
    :showErrorFeedbackMessage="showErrorFeedbackMessage"
    :showSuccessFeedbackMessage="showSuccessFeedbackMessage"
    :feedbackMessage="feedbackMessage"
    :infoIcon="infoIcon"
    :infoText="infoText"
    :size="size"
  >
    <template>
      <div class="input">
        <DatePicker
          v-bind="calendarPluginOptions"
          :min-date="
            (calendarPluginOptions && calendarPluginOptions.minDate) || minDate
          "
          :value="value"
          locale="pt-br"
          :popover="{
            placement: 'top-end',
            visibility: 'click'
          }"
          title-position="left"
          @input="$emit('input', $event)"
        >
          <template v-slot="{ inputValue, togglePopover }">
            <div class="datepicker-field-wrapper">
              <!-- SE É DO TIPO RANGE -->
              <input
                v-if="inputValue.start && inputValue.end"
                :value="getFormatedDate(inputValue.start, inputValue.end)"
                :placeholder="placeholder"
                class="input--field"
                readonly
                @click="togglePopover()"
              />
              <!-- SE É SELEÇÃO ÚNICA -->
              <input
                v-else-if="typeof inputValue !== 'object' && inputValue"
                :value="getFormatedDate(inputValue)"
                :placeholder="placeholder"
                class="input--field"
                readonly
                @click="togglePopover()"
              />
              <!-- SEM VALOR -->
              <input
                v-else
                :placeholder="placeholder"
                class="input--field"
                readonly
                @click="togglePopover()"
              />

              <button class="icon-button" @click="togglePopover()">
                <Icon icon="calendar-light" />
              </button>
            </div>
          </template>
        </DatePicker>
      </div>
      <!-- LIMPAR CAMPO -->
      <transition name="fade">
        <button
          v-if="value && !error && !success"
          class="input--clear"
          type="button"
          @click="$emit('input', '')"
        >
          {{ clearButtonText }}
        </button>
      </transition>
    </template>
  </FormField>
</template>

<script>
/**
 * Componente de datepicker para formulários
 */

import { getRandomInt } from '@/helpers/math'
import FormField from '../patterns/FormField'
import DatePicker from 'v-calendar/lib/components/date-picker.umd'

export default {
  name: 'FormDatepicker',
  components: {
    FormField,
    DatePicker
  },
  props: {
    /** Label do campo */
    label: {
      type: String,
      default: 'Label do input'
    },
    /** Placeholder do campo */
    placeholder: {
      type: String,
      default: 'Insira o conteúdo'
    },
    /** Tipo do campo */
    type: {
      type: String,
      default: 'text'
    },
    /** Se o campo está com erro de validação */
    error: {
      type: Boolean,
      default: false
    },
    /** Se o campo passou na validação */
    success: {
      type: Boolean,
      default: false
    },
    /** Se deve mostrar uma mensagem de feedback caso ocorra erro */
    showErrorFeedbackMessage: {
      type: Boolean,
      default: false
    },
    /** Se deve mostrar uma mensagem de feedback caso ocorra sucesso */
    showSuccessFeedbackMessage: {
      type: Boolean,
      default: false
    },
    /** Texto da mensagem de feedback */
    feedbackMessage: {
      type: String,
      default: 'Campo obrigatório'
    },
    /** Nome do campo */
    name: {
      type: String,
      default: ''
    },
    /** Valor do campo */
    value: {
      type: [String, Number, Object, Array, Date],
      default: ''
    },
    /** Se é obrigatório */
    required: {
      type: Boolean,
      default: false
    },
    /** Se é apenas leitura */
    readonly: {
      type: Boolean,
      default: false
    },
    /** Se deve apresentar um ícone de informação sobre o campo */
    infoIcon: {
      type: Boolean,
      default: false
    },
    /** Texto de informação */
    infoText: {
      type: String,
      default: 'Informação sobre o campo'
    },
    /** Sufixo que aparecerá após o valor do input */
    sufix: {
      type: String,
      default: ''
    },
    /** Texto do botão limpar */
    clearButtonText: {
      type: String,
      default: 'Limpar'
    },
    /**
     * Opções do plugin de calendário
     * @see https://vcalendar.io/api/v2.0/calendar.html#props
     */
    vcalendarOptions: {
      type: Object,
      default: () => {}
    },
    /**
     * Menor data selecionável
     * @default {Date} - data atual
     */
    minDate: {
      type: [String, Date],
      default: () => new Date()
    },
    /**
     * Largura do campo
     * small, large, fluid
     */
    size: {
      type: String,
      default: 'fluid'
    }
  },
  data() {
    return {
      defaultId: `form-input-${getRandomInt(0, 99999)}`
    }
  },
  computed: {
    /** retorna as props definidas pelo usuário */
    calendarPluginOptions() {
      return this.vcalendarOptions
    }
  },
  methods: {
    getFormatedDate(start, end) {
      return `${start ?? ''}${start && end ? ' - ' : ''}${end ?? ''}`
    }
  }
}
</script>

<style lang="scss" scoped>
.input {
  display: flex;
  position: relative;

  & > span {
    display: block;
    width: 100%;
    position: relative;
  }

  &--sufix {
    position: absolute;
    right: 0;
    font-size: font-size('size-medium');
  }

  &--field {
    display: block;
    height: $input-height;
    padding: 0;
    line-height: 21px;
    width: 100%;
    border: none;
    border-bottom: 1px solid $gray-darkest;
    font-size: font-size('size-medium');
    font-weight: font-weight('weight-normal');

    &[type='number']::-webkit-inner-spin-button,
    &[type='number']::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    @include placeholder {
      font-size: font-size('size-medium');
      font-weight: font-weight('weight-light');
      color: $gray-mid;
    }

    &:focus {
      border-color: $blue-2;
      border-bottom-width: 2px;
    }

    &__error {
      border-bottom-color: $error;
      border-bottom-width: 2px;
    }
  }

  &--clear {
    @include reset-button;
    position: absolute;
    left: 0;
    top: calc(100% + 3px);
    line-height: 14px;
    font-size: font-size('size-x-small');
    color: $blue;
    z-index: 1;
  }
}

.datepicker {
  &-field-wrapper {
    display: flex;
    align-items: center;
    position: relative;

    .icon-button {
      position: absolute;
      right: 0;
      padding: 0;
      color: $gray-dark;
      border: none;
      background: none;
      .icon {
        width: 14px;
      }
    }
  }
}

.form-field {
  ::v-deep {
    .vc-popover-content-wrapper {
      left: calc(100% - 13px) !important;
      transform: translate(-50%, -33px) !important;

      .vc-popover-caret {
        right: auto;
        left: 50%;
        transform: translateX(-50%) translateY(-50%) rotate(-135deg);
      }
    }
  }
}
</style>
